import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PS } from '../theme';
import { Avatar, Chip, Wordmark, VisibilityBadge } from '../lib/ui';
import { Icon } from '../lib/icons';
import { fmt, thb } from '../lib/format';
import { useFetch } from '../lib/useFetch';
import HandsBoard from '../components/HandsBoard';
import api from '../lib/api';
import { notifyError, notifySuccess } from '../lib/toast';
import { track } from '../lib/analytics';

const payColor = (s) => ({ paid: 'oklch(0.48 0.08 155)', host: 'oklch(0.48 0.08 155)', won: 'oklch(0.62 0.12 38)', late: 'oklch(0.52 0.12 30)' }[s] || 'oklch(0.72 0.12 82)');

function LegendDot({ color, label }) {
  return <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}><div style={{ width: 8, height: 8, borderRadius: 2, background: color }} /><span>{label}</span></div>;
}

function Kpi({ l, v, sub }) {
  return (
    <div style={{ background: PS.surface2, border: `1px solid ${PS.line}`, borderRadius: 14, padding: 18 }}>
      <div style={{ fontSize: 10.5, color: PS.textMuted, textTransform: 'uppercase', letterSpacing: 1 }}>{l}</div>
      <div className="num" style={{ fontSize: 26, fontWeight: 500, marginTop: 8, letterSpacing: -0.5 }}>{v}</div>
      <div style={{ fontSize: 11, color: PS.textMuted, marginTop: 4 }}>{sub}</div>
    </div>
  );
}

function LiveRound({ live, kpi, onRecord }) {
  return (
    <div style={{ background: PS.ink, color: PS.page, borderRadius: 14, padding: 22, position: 'relative', overflow: 'hidden' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '3px 9px', borderRadius: 999, background: 'rgba(168,74,50,0.3)', fontSize: 10.5, fontWeight: 700, color: 'oklch(0.82 0.1 38)' }}>
          <div className="ps-live-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: 'oklch(0.75 0.12 38)' }} />LIVE · รอบ {live?.round_number ?? '—'}
        </div>
        <div style={{ fontSize: 11.5, color: 'rgba(245,241,234,0.6)' }}>{live?.scheduled_date} · {live?.venue || ''}</div>
      </div>
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-end', marginTop: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 10.5, color: 'rgba(245,241,234,0.55)', letterSpacing: 1, textTransform: 'uppercase' }}>มูลค่ากองงวดนี้</div>
          <div className="num" style={{ fontSize: 52, fontWeight: 500, letterSpacing: -1.5, lineHeight: 1, marginTop: 6 }}>{thb(kpi.due_this_round)}</div>
          <div style={{ fontSize: 11.5, color: 'rgba(245,241,234,0.6)', marginTop: 8 }}>รอบันทึกผลการเปียจากท้าวแชร์</div>
        </div>
        <button data-testid="host-record-btn" onClick={onRecord} className="ps-press" style={{ height: 44, padding: '0 20px', background: 'oklch(0.72 0.12 82)', color: PS.ink, borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 700, fontFamily: PS.fontUI }}>บันทึกผลเปีย</button>
      </div>
      <div style={{ position: 'absolute', top: -30, right: -40, width: 180, height: 180, border: '1px solid rgba(245,241,234,0.08)', transform: 'rotate(45deg)' }} />
    </div>
  );
}

function PaymentStatus({ members, kpi, circleId, onChange }) {
  const [busy, setBusy] = React.useState(null);
  // payable = สมาชิกที่ต้องจ่ายงวดนี้ (ไม่นับท้าว และคนที่รับเปียไปแล้ว/มือตาย)
  const payable = members.filter(m => m.role !== 'host' && m.pay_status !== 'won');
  const unpaid = payable.filter(m => m.pay_status !== 'paid');

  const togglePaid = async (m) => {
    const next = m.pay_status !== 'paid';
    setBusy(m.user_id);
    try {
      await api.post(`/circles/${circleId}/members/${m.user_id}/mark-paid`, { paid: next });
      track(next ? 'payment_marked' : 'payment_unmarked', { circle_id: circleId });
      if (next) notifySuccess(`บันทึกว่า ${m.name} จ่ายแล้ว · เก็บเป็นหลักฐาน`);
      onChange?.();
    } catch (e) {
      notifyError(e?.response?.data?.detail || 'ทำรายการไม่สำเร็จ');
    } finally { setBusy(null); }
  };

  const remind = async () => {
    setBusy('remind');
    try {
      const res = await api.post(`/circles/${circleId}/remind-unpaid`);
      track('reminder_sent', { circle_id: circleId, count: res.data?.reminded });
      notifySuccess(`ส่งเตือนแล้ว ${res.data?.reminded ?? 0} คน`);
      onChange?.();
    } catch (e) {
      notifyError(e?.response?.data?.detail || 'ส่งเตือนไม่สำเร็จ');
    } finally { setBusy(null); }
  };

  return (
    <div style={{ background: PS.surface2, border: `1px solid ${PS.line}`, borderRadius: 14, padding: 22 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <div style={{ fontSize: 10.5, color: PS.textMuted, textTransform: 'uppercase', letterSpacing: 1 }}>สถานะการจ่ายงวดนี้</div>
          <div className="num" style={{ fontSize: 24, marginTop: 6 }}>{kpi.paid_count} <span style={{ fontSize: 14, color: PS.textMuted }}>/ {kpi.members} คน</span></div>
        </div>
        <div style={{ fontSize: 11.5, color: PS.ok, fontWeight: 600 }}>ยอดรวม {thb(kpi.due_this_round)}</div>
      </div>
      <div style={{ display: 'flex', gap: 3, marginTop: 16, flexWrap: 'wrap' }}>
        {members.map((m, i) => (
          <div key={i} title={m.name} style={{ flex: '1 0 18px', height: 34, borderRadius: 4, background: payColor(m.role === 'host' ? 'host' : m.pay_status), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#fff', fontFamily: PS.fontMono, fontWeight: 600 }}>{m.slot_number}</div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 14, marginTop: 12, fontSize: 11, color: PS.textMuted, flexWrap: 'wrap' }}>
        <LegendDot color="oklch(0.48 0.08 155)" label={`จ่ายแล้ว (${kpi.paid_count})`} />
        <LegendDot color="oklch(0.62 0.12 38)" label="รับเงิน" />
        <LegendDot color="oklch(0.52 0.12 30)" label={`ค้าง (${kpi.late_count})`} />
      </div>

      {/* One-tap: ทำเครื่องหมายว่าจ่ายแล้ว → บันทึกเป็นหลักฐานอัตโนมัติ */}
      {payable.length > 0 && (
        <div style={{ marginTop: 16, borderTop: `1px solid ${PS.lineSoft}`, paddingTop: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>ยืนยันการชำระ (แตะปุ่มเดียว = เก็บหลักฐานให้)</div>
            {unpaid.length > 0 && (
              <button data-testid="host-remind-unpaid" disabled={!!busy} onClick={remind} className="ps-press"
                style={{ height: 32, padding: '0 12px', borderRadius: 8, border: `1px solid ${PS.line}`, background: 'transparent', color: PS.text, fontSize: 11.5, fontWeight: 600, fontFamily: PS.fontUI, display: 'flex', alignItems: 'center', gap: 6, opacity: busy ? 0.5 : 1 }}>
                <Icon.info size={13} stroke={PS.ink} />{busy === 'remind' ? 'กำลังเตือน…' : `เตือนที่ค้าง (${unpaid.length})`}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {payable.map((m) => {
              const paid = m.pay_status === 'paid';
              return (
                <div key={m.user_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 10, background: PS.surface, border: `1px solid ${PS.lineSoft}` }}>
                  <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <span style={{ color: PS.textDim, fontFamily: PS.fontMono, fontSize: 11 }}>#{String(m.slot_number).padStart(2, '0')}</span> {m.name}
                  </div>
                  <button data-testid={`host-pay-toggle-${m.user_id}`} disabled={!!busy} onClick={() => togglePaid(m)} className="ps-press"
                    style={{ height: 34, padding: '0 14px', borderRadius: 9, border: paid ? 'none' : `1px solid ${PS.ink}`, background: paid ? 'oklch(0.48 0.08 155)' : 'transparent', color: paid ? '#fff' : PS.ink, fontSize: 12, fontWeight: 600, fontFamily: PS.fontUI, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, opacity: busy ? 0.5 : 1 }}>
                    {busy === m.user_id ? '…' : paid ? <><Icon.check size={13} stroke="#fff" sw={2.5} />จ่ายแล้ว</> : 'ทำเครื่องหมายว่าจ่าย'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {kpi.late_count > 0 && (
        <div style={{ marginTop: 16, padding: 12, background: 'oklch(0.52 0.12 30 / 0.06)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon.info size={16} stroke="oklch(0.52 0.12 30)" />
          <div style={{ flex: 1, fontSize: 11.5, color: PS.text }}>มีสมาชิกค้างชำระ {kpi.late_count} คน — สามารถขอซองหลักฐานได้</div>
        </div>
      )}
    </div>
  );
}

function MembersTable({ members }) {
  const statusLabel = { paid: { label: 'จ่ายแล้ว', tone: 'green' }, won: { label: 'รับเงินแล้ว', tone: 'terra' }, late: { label: 'ค้างชำระ', tone: 'warn' } };
  const creditColor = (c) => c >= 780 ? 'oklch(0.48 0.08 155)' : c >= 720 ? PS.ink : c >= 680 ? 'oklch(0.58 0.1 80)' : 'oklch(0.52 0.12 30)';
  return (
    <div style={{ background: PS.surface2, border: `1px solid ${PS.line}`, borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${PS.line}` }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>สมาชิกในวง</div>
        <div style={{ fontSize: 11.5, color: PS.textMuted, marginTop: 2 }}>{members.length} คน · ทุกคนลงนามสัญญาแล้ว</div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 640 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2.2fr 1fr 1fr 1fr 2fr', padding: '10px 20px', fontSize: 10.5, color: PS.textMuted, textTransform: 'uppercase', letterSpacing: 1, background: PS.surface, borderBottom: `1px solid ${PS.line}` }}>
            <div>สมาชิก</div><div>เครดิต</div><div>ตรงเวลา</div><div>งวดที่จ่าย</div><div>สถานะงวดนี้</div>
          </div>
          {members.map((m, i) => {
            const sl = statusLabel[m.pay_status] || { label: m.role === 'host' ? 'ท้าวแชร์' : 'รอ', tone: 'default' };
            return (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '2.2fr 1fr 1fr 1fr 2fr', padding: '14px 20px', alignItems: 'center', borderBottom: i < members.length - 1 ? `1px solid ${PS.lineSoft}` : 'none', fontSize: 12.5 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
                  <Avatar name={m.name} size={32} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</div>
                    <div style={{ fontSize: 10.5, color: PS.textMuted }}>ลูกแชร์ #{String(m.slot_number).padStart(2, '0')} · {m.phone}</div>
                  </div>
                </div>
                <div>
                  <div className="num" style={{ fontSize: 14, color: creditColor(m.credit_score), fontWeight: 500 }}>{m.credit_score}</div>
                  <div style={{ width: 44, height: 3, background: PS.line, borderRadius: 999, overflow: 'hidden', marginTop: 4 }}><div style={{ width: (m.credit_score / 850) * 100 + '%', height: '100%', background: creditColor(m.credit_score) }} /></div>
                </div>
                <div className="num" style={{ fontSize: 14 }}>{m.on_time_pct}%</div>
                <div style={{ fontSize: 12 }}><b className="num">{m.paid_rounds}</b><span style={{ color: PS.textDim }}> งวด</span></div>
                <div><Chip tone={sl.tone}>{sl.label}</Chip><div style={{ fontSize: 10.5, color: PS.textMuted, marginTop: 4 }}>{m.note}</div></div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function RoundsTimeline({ rounds, count }) {
  return (
    <div style={{ background: PS.surface2, border: `1px solid ${PS.line}`, borderRadius: 14, padding: 22 }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>ไทม์ไลน์การเปีย {count} งวด</div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(count, 24)}, 1fr)`, gap: 4, marginBottom: 20 }}>
        {Array.from({ length: Math.min(count, 24) }).map((_, i) => {
          const r = rounds.find(x => x.round_number === i + 1);
          const st = r ? r.status : 'future';
          const color = st === 'done' ? 'oklch(0.48 0.08 155)' : st === 'live' ? 'oklch(0.62 0.12 38)' : PS.line;
          return <div key={i} style={{ height: 40, background: color, borderRadius: 4, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 4, color: st === 'future' ? PS.textDim : '#fff', fontFamily: PS.fontMono, fontSize: 9, fontWeight: 600 }}>{i + 1}</div>;
        })}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 520 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '0.6fr 1.3fr 1.6fr 1fr 1fr', padding: '6px 0', fontSize: 10.5, color: PS.textMuted, textTransform: 'uppercase', letterSpacing: 1 }}>
            <div>งวด</div><div>วันที่</div><div>ผู้ชนะเปีย</div><div style={{ textAlign: 'right' }}>ดอกเปีย</div><div style={{ textAlign: 'right' }}>ยอดรับ</div>
          </div>
          {rounds.map((r, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '0.6fr 1.3fr 1.6fr 1fr 1fr', padding: '10px 0', fontSize: 12.5, borderTop: `1px solid ${PS.lineSoft}`, alignItems: 'center', opacity: r.status === 'future' ? 0.5 : 1 }}>
              <div className="num" style={{ fontSize: 14 }}>{r.round_number}</div>
              <div>{r.scheduled_date}</div>
              <div>{r.status === 'live' ? <Chip tone="terra">กำลังเกิดขึ้น</Chip> : r.status === 'future' ? <Chip tone="outline">รอ</Chip> : r.winner_name}</div>
              <div className="num" style={{ textAlign: 'right' }}>{r.winning_bid != null ? thb(r.winning_bid) : '—'}</div>
              <div className="num" style={{ textAlign: 'right', fontWeight: 500 }}>{r.pot_amount != null ? thb(r.pot_amount) : '—'}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PendingRequests({ circleId, requests, onChange }) {
  const [busy, setBusy] = React.useState(null);
  const act = async (userId, kind) => {
    setBusy(userId + kind);
    try {
      await api.post(`/circles/${circleId}/members/${userId}/${kind}`);
      onChange?.();
    } catch (e) {
      notifyError(e?.response?.data?.detail || 'เกิดข้อผิดพลาด');
    } finally { setBusy(null); }
  };
  return (
    <div data-testid="host-pending-card" style={{ background: PS.surface2, border: `1px solid oklch(0.72 0.12 82)`, borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${PS.line}`, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 28, height: 28, borderRadius: 9, background: 'oklch(0.72 0.12 82 / 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon.user size={15} stroke="oklch(0.5 0.12 70)" /></div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>คำขอเข้าร่วมรออนุมัติ</div>
          <div style={{ fontSize: 11.5, color: PS.textMuted, marginTop: 2 }}>{requests.length} คำขอ · ตรวจเครดิตก่อนอนุมัติ — ทุกการตัดสินใจถูกบันทึกเป็นหลักฐาน</div>
        </div>
      </div>
      {requests.map((m, i) => (
        <div key={m.user_id} data-testid={`host-pending-row-${m.user_id}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', borderBottom: i < requests.length - 1 ? `1px solid ${PS.lineSoft}` : 'none', flexWrap: 'wrap' }}>
          <Avatar name={m.name} size={36} />
          <div style={{ flex: 1, minWidth: 140 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{m.name}</div>
            <div style={{ fontSize: 11, color: PS.textMuted, marginTop: 2 }}>เครดิต <b className="num" style={{ color: PS.text }}>{m.credit_score}</b> · ตรงเวลา <b className="num" style={{ color: PS.text }}>{m.on_time_pct}%</b></div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button data-testid={`host-reject-${m.user_id}`} disabled={!!busy} onClick={() => act(m.user_id, 'reject')} className="ps-press" style={{ height: 38, padding: '0 16px', background: 'transparent', border: `1px solid ${PS.line}`, borderRadius: 10, fontSize: 12.5, fontWeight: 600, fontFamily: PS.fontUI, color: PS.text, opacity: busy ? 0.5 : 1 }}>ปฏิเสธ</button>
            <button data-testid={`host-approve-${m.user_id}`} disabled={!!busy} onClick={() => act(m.user_id, 'approve')} className="ps-press" style={{ height: 38, padding: '0 18px', background: PS.ink, color: PS.page, border: 'none', borderRadius: 10, fontSize: 12.5, fontWeight: 600, fontFamily: PS.fontUI, display: 'flex', alignItems: 'center', gap: 6, opacity: busy ? 0.5 : 1 }}>
              <Icon.check size={14} stroke={PS.page} sw={2} />{busy === m.user_id + 'approve' ? 'กำลังอนุมัติ…' : 'อนุมัติ'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function HostConsole() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data, reload, error } = useFetch(`/dashboard/host?circle_id=${id}`, [id]);

  const scrollRef = useRef(null);
  const membersRef = useRef(null);
  const auctionRef = useRef(null);
  const payRef = useRef(null);
  const [activeNav, setActiveNav] = useState(0);

  if (error && !data) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', justifyContent: 'center', background: PS.page, padding: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: PS.text }}>โหลดคอนโซลไม่สำเร็จ</div>
      <div style={{ fontSize: 12.5, color: PS.textMuted }}>วงนี้อาจไม่มีอยู่ หรือเกิดข้อผิดพลาดชั่วคราว</div>
      <button data-testid="host-error-retry" onClick={reload} className="ps-press" style={{ height: 42, padding: '0 20px', background: PS.ink, color: PS.page, border: 'none', borderRadius: 12, fontSize: 13.5, fontWeight: 600, fontFamily: PS.fontUI }}>ลองอีกครั้ง</button>
    </div>
  );
  if (!data) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: PS.page }}><div className="ps-live-dot" style={{ color: PS.textMuted }}>กำลังโหลดคอนโซล…</div></div>;
  const { circle, members, rounds, kpi, live_round } = data;
  const pending = data.pending_requests || [];

  const NAV = [
    { label: 'ภาพรวม', icon: Icon.home, to: 'top' },
    { label: 'สมาชิก', icon: Icon.user, to: membersRef },
    { label: 'การประมูล', icon: Icon.arrowUp, to: auctionRef },
    { label: 'การชำระเงิน', icon: Icon.wallet, to: payRef },
    { label: 'สัญญา', icon: Icon.doc, route: `/app/circle/${id}/vault` },
  ];

  const goNav = (i, item) => {
    setActiveNav(i);
    if (item.route) { nav(item.route); return; }
    if (item.to === 'top') { scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    item.to?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: PS.page, fontFamily: PS.fontUI, color: PS.text }}>
      {/* Sidebar (desktop) */}
      <div role="navigation" aria-label="เมนูคอนโซลท้าวแชร์" style={{ width: 220, background: PS.ink, color: PS.page, display: 'flex', flexDirection: 'column', padding: '18px 0', flexShrink: 0 }} className="ps-host-sidebar">
        <div style={{ padding: '0 20px 20px', borderBottom: '1px solid rgba(245,241,234,0.08)' }}>
          <Wordmark color={PS.page} size={20} />
          <div style={{ fontSize: 10.5, color: 'rgba(245,241,234,0.55)', marginTop: 4, letterSpacing: 1, textTransform: 'uppercase' }}>Host Console</div>
        </div>
        <div style={{ padding: '14px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {NAV.map((item, i) => {
            const IcoC = item.icon;
            const a = i === activeNav;
            return (
              <button key={i} data-testid={`host-nav-${i}`} onClick={() => goNav(i, item)} className="ps-press"
                style={{ padding: '9px 12px', borderRadius: 10, border: 'none', textAlign: 'left', cursor: 'pointer', width: '100%', background: a ? 'rgba(245,241,234,0.1)' : 'transparent', color: a ? PS.page : 'rgba(245,241,234,0.7)', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, fontWeight: a ? 600 : 500, fontFamily: PS.fontUI }}>
                <IcoC size={16} stroke={a ? PS.page : 'rgba(245,241,234,0.7)'} />{item.label}
              </button>
            );
          })}
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ margin: '0 12px', padding: 12, background: 'rgba(245,241,234,0.06)', borderRadius: 10 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 10, letterSpacing: 0.8, textTransform: 'uppercase', color: 'rgba(245,241,234,0.55)' }}><Icon.shield size={12} stroke="oklch(0.75 0.08 155)" />Compliance</div>
          <div style={{ fontSize: 10.5, color: 'rgba(245,241,234,0.75)', lineHeight: 1.35, marginTop: 6 }}>พ.ร.บ. การเล่นแชร์ พ.ศ. 2534 — อยู่ในเกณฑ์</div>
        </div>
        <button data-testid="host-sidebar-back" onClick={() => nav('/app')} className="ps-press"
          style={{ padding: '14px 12px 0', display: 'flex', gap: 10, alignItems: 'center', background: 'transparent', border: 'none', width: '100%', textAlign: 'left', fontFamily: PS.fontUI }}>
          <Icon.chevronL size={16} stroke="rgba(245,241,234,0.7)" /><div style={{ fontSize: 12, color: 'rgba(245,241,234,0.7)' }}>กลับแอป</div>
        </button>
      </div>

      <div ref={scrollRef} style={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
        {/* Mobile nav bar (shown <880px where the sidebar is hidden) */}
        <nav className="ps-host-mobilebar" aria-label="เมนูคอนโซลท้าวแชร์"
          style={{ position: 'sticky', top: 0, zIndex: 20, background: PS.ink, color: PS.page }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px' }}>
            <button data-testid="host-mobile-back" aria-label="กลับไปหน้าแอป" onClick={() => nav('/app')} className="ps-press"
              style={{ width: 36, height: 36, borderRadius: 10, border: 'none', background: 'rgba(245,241,234,0.1)', color: PS.page, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon.chevronL size={18} stroke={PS.page} />
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 9.5, color: 'rgba(245,241,234,0.55)', letterSpacing: 1, textTransform: 'uppercase' }}>Host Console</div>
              <div style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{circle.name}</div>
            </div>
            <button data-testid="host-mobile-create" aria-label="ตั้งวงใหม่" onClick={() => nav('/app/create')} className="ps-press"
              style={{ width: 36, height: 36, borderRadius: 10, border: 'none', background: 'oklch(0.72 0.12 82)', color: PS.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon.plus size={18} stroke={PS.ink} sw={2} />
            </button>
          </div>
          <div style={{ display: 'flex', gap: 6, padding: '0 12px 10px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {NAV.map((item, i) => {
              const IcoC = item.icon; const a = i === activeNav;
              return (
                <button key={i} data-testid={`host-mobile-nav-${i}`} onClick={() => goNav(i, item)} className="ps-press"
                  style={{ flexShrink: 0, padding: '7px 13px', borderRadius: 999, border: 'none', background: a ? PS.page : 'rgba(245,241,234,0.1)', color: a ? PS.ink : 'rgba(245,241,234,0.8)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, fontFamily: PS.fontUI }}>
                  <IcoC size={14} stroke={a ? PS.ink : 'rgba(245,241,234,0.8)'} />{item.label}
                </button>
              );
            })}
          </div>
        </nav>

        <div style={{ padding: '20px 32px', borderBottom: `1px solid ${PS.line}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: PS.surface2, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: PS.textMuted, textTransform: 'uppercase', letterSpacing: 1 }}>ภาพรวม</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: -0.3 }}>{circle.name}</div>
              <VisibilityBadge visibility={circle.visibility} />
              <button data-testid="host-visibility-toggle" onClick={async () => {
                const nextVis = circle.visibility === 'public' ? 'private' : 'public';
                try { await api.patch(`/circles/${id}/visibility`, { visibility: nextVis }); reload(); }
                catch { notifyError('เปลี่ยนการมองเห็นไม่สำเร็จ'); }
              }} className="ps-press" style={{ padding: '5px 11px', borderRadius: 8, border: `1px solid ${PS.line}`, background: 'transparent', color: PS.textMuted, fontSize: 11, fontWeight: 600, fontFamily: PS.fontUI }}>
                {circle.visibility === 'public' ? 'ทำเป็นวงส่วนตัว' : 'เปิดเป็นวงสาธารณะ'}
              </button>
            </div>
          </div>
          <button data-testid="host-create-btn" onClick={() => nav('/app/create')} className="ps-press" style={{ padding: '0 14px', height: 36, background: PS.ink, color: PS.page, borderRadius: 10, border: 'none', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, fontFamily: PS.fontUI }}><Icon.plus size={14} stroke={PS.page} sw={2} /> ตั้งวงใหม่</button>
        </div>

        <div style={{ padding: '24px 32px 0', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          <Kpi l="ยอดวงรวม" v={thb(kpi.total_amount)} sub={`${kpi.rounds_count} งวด · ${thb(kpi.amount_per_round)}/งวด`} />
          <Kpi l="ครบกำหนดงวดนี้" v={thb(kpi.due_this_round)} sub={`${kpi.members} สมาชิก`} />
          <Kpi l="ตรงเวลาเฉลี่ย" v={`${kpi.on_time_avg}%`} sub={`${kpi.late_count} ค้าง · ติดตามแล้ว`} />
          <Kpi l="เครดิตเฉลี่ย" v={kpi.avg_credit} sub="ของสมาชิกในวง" />
        </div>

        <div ref={payRef} style={{ padding: '20px 32px 0', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, scrollMarginTop: 16 }}>
          <LiveRound live={live_round} kpi={kpi} onRecord={() => nav(`/app/circle/${id}/bid`)} />
          <PaymentStatus members={members} kpi={kpi} circleId={id} onChange={reload} />
        </div>

        {pending.length > 0 && (
          <div style={{ padding: '20px 32px 0' }}>
            <PendingRequests circleId={id} requests={pending} onChange={reload} />
          </div>
        )}

        <div ref={membersRef} style={{ padding: '20px 32px 0', scrollMarginTop: 16 }}><MembersTable members={members} /></div>
        <div style={{ padding: '20px 32px 0' }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>สถานะมือ · ใครเปียแล้วบ้าง</div>
          <HandsBoard circleId={id} />
        </div>
        <div ref={auctionRef} style={{ padding: '20px 32px 32px', scrollMarginTop: 16 }}><RoundsTimeline rounds={rounds} count={kpi.rounds_count} /></div>
      </div>
    </div>
  );
}
